import { CUSTOM_ELEMENTS_SCHEMA, Component, Inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../environments/environment';
import { BasePageComponent } from '../../../../../../shared/base/base-page.component';
import { RESPONSE_CODES } from '../../../../../../shared/constants/common.constants';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { BankcardRecapFormField, BankcardRecapFormFieldComponent } from '../../../../../../shared/payments/bankcard-recap-form-field/bankcard-recap-form-field.component';
import { FormUtil } from '../../../../../../shared/payments/form-util';
import { PaymentFormGroup } from '../../../../../../shared/payments/payment-form-group';
import { RecapFormField, RecapFormFieldComponent } from '../../../../../../shared/payments/recap-form-field/recap-form-field.component';
import { BillingEventService } from '../../../../../../shared/service/ebi-service/billing-events.service';
import { EBIEventType } from '../../../../../../shared/service/ebi-service/ebi-model';
import { SessionService } from '../../../../../../shared/session/session.service';
import { UrlUtil } from '../../../../../../shared/url-util/url-util.service';
import { PaymentItem } from '../../../../shared/models/payment-item.model';
import { ManagePaymentPreferencesPageTitle } from '../../../shared/models/manage-payment-preferences-page-title.constant';
import { EditAutomaticPaymentFlowService } from '../services/edit-automatic-payments-flow-service';
import { EditAutomaticPaymentsReview } from './edit-automatic-payments-review.model';
import { EditAutomaticPaymentsReviewService } from './edit-automatic-payments-review.service';
import { ContentfulMapperService } from '@nationwide/dgs-angular-billing-common';
import { CelebrusTrackingService } from '../../../../../../shared/service/celebrus-tracking/celebrus-tracking.service';
import { WaitMessageComponent, InterpolationComponent, ButtonWrapperComponent } from '@nationwide/internet-servicing-angular-components';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FocusOnInitDirective } from '../../../../../../shared/attr-directives/focus-on-init.directive';
import { PaymentSubHeaderComponent } from '../../../../../../shared/payments/payment-sub-header/payment-sub-header.component';
import { EmailFormControlComponent } from '../../../../../../shared/payments/email-form-control/email-form-control.component';
import { EzPayAuthorizationComponent } from '../../../../../../shared/payments/ez-pay-authorization/ez-pay-authorization.component';

@Component({
    selector: 'app-edit-automatic-payments-review',
    templateUrl: './edit-automatic-payments-review.component.html',
    styleUrls: ['./edit-automatic-payments-review.component.scss'],
    imports: [WaitMessageComponent, FormsModule, ReactiveFormsModule, FocusOnInitDirective, PaymentSubHeaderComponent, RecapFormFieldComponent, BankcardRecapFormFieldComponent, EmailFormControlComponent, EzPayAuthorizationComponent, InterpolationComponent, ButtonWrapperComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EditAutomaticPaymentsReviewComponent extends BasePageComponent implements OnInit {
    pageName = ManagePaymentPreferencesPageTitle.automaticPayments.edit.review;
    paymentDetailsReviewFields: RecapFormField[] | BankcardRecapFormField[] = [];
    loadComplete = signal(false);
    acknowledgedCheckboxDisabled: boolean;
    disableSubmit = signal(false);
    generateAutoPayAuthorizationErrorContent: string;
    canShowWaitSpinner = signal(true);

    constructor(
        private review: EditAutomaticPaymentsReview,
        router: Router,
        private editAutomaticPaymentsReviewService: EditAutomaticPaymentsReviewService,
        private editAutomaticPaymentsFlowService: EditAutomaticPaymentFlowService,
        private readonly celebrusTrackingService: CelebrusTrackingService,
        private formUtils: FormUtil,
        @Inject('window') private window: Window,
        protected session: SessionService,
        private eventService: BillingEventService,
        @Inject('logger') private logger: LoggerService,
        private urlUtil: UrlUtil,
        private contentfulMapperService: ContentfulMapperService
    ) {
        super(session, router);
    }

    ngOnInit(): void {
        try {
            super.ngOnInit();
            this.session.currentPage = this.pageName;
            this.review.initialize();
            this.paymentDetailsReviewFields = this.review.paymentDetailsReviewFields;
            this.loadComplete.set(true);
            this.celebrusTrackingService.storePaymentPreferenceData({ isBankAccount: this.review.isBankAccountFlow });
            this.editAutomaticPaymentsReviewService.getAutomaticPaymentsAuthorizationForm().subscribe({
                next: (generateAgreementsResponse) => {
                    this.session.recurringAuthorizationAgreement = generateAgreementsResponse.agreement;
                    this.editAutomaticPaymentsReviewService.wasAutoPayAuthorizationFormSuccessfullyGenerated = true;
                    this.canShowWaitSpinner.set(false);
                },
                error: () => {
                    this.editAutomaticPaymentsReviewService.wasAutoPayAuthorizationFormSuccessfullyGenerated = false;
                    this.disableSubmit.set(true);
                    this.setGenerateAuthAgreementErrorMessages();
                    this.canShowWaitSpinner.set(false);
                }
            });
        } catch (error) {
            this.logger.error('Edit Automatic Payments Review - load failed: ', {
                error: error.stack || error
            });
        }
    }

    setGenerateAuthAgreementErrorMessages(): void {
        this.contentfulMapperService.getContentfulBillingContent(environment.PRODUCTION).subscribe((contentfulBillingContent) => {
            this.generateAutoPayAuthorizationErrorContent = contentfulBillingContent?.NuxeoGenerateFailureMessage?.content;
        });
    }

    get paymentFormReview(): PaymentFormGroup {
        return this.review.editAutomaticPaymentForm;
    }

    navigateToEditAutomaticPayments(): void {
        const reviewPageUrl = environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.AUTOMATIC_PAYMENTS.EDIT.LANDING(
            this.urlUtil.hashParamsString);
        this.router.navigateByUrl(`${reviewPageUrl}&fromPage=review`);
    }

    navigateToPaymentPreferences(): void {
        this.router.navigateByUrl(environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.paymentPreferences(
            this.urlUtil.hashParamsString
        ));
    }

    navigateToConfirmationPage(): void {
        this.router.navigateByUrl(
            environment.ISB.ENDPOINTS.MANAGE_PAYMENT_PREFERENCES.AUTOMATIC_PAYMENTS.EDIT.CONFIRMATION(
                this.urlUtil.hashParamsString
            ));
    }

    get canDisplayAuthorizationAgreement(): boolean {
        let canDisplayAuthorizationAgreements = false;
        if (!this.canShowWaitSpinner()) {
            canDisplayAuthorizationAgreements = this.editAutomaticPaymentsReviewService.wasAutoPayAuthorizationFormSuccessfullyGenerated;
        }
        return canDisplayAuthorizationAgreements;
    }

    onSubmitEditAutomaticPayment(): void {
        this.sendOnClickOfSubmitButtonEbiEvent();
        this.formUtils.touchAllFormControls(this.paymentFormReview.group);
        if (this.paymentFormReview.group.valid && !(this.canDisplayAuthorizationAgreement && this.review.addEzPayAuthGroupControls.disabled)) {
            this.loadComplete.set(false);
            this.editAutomaticPaymentsReviewService.storeAgreementAcknowledgementsForRecurring().subscribe({
                next: () => {
                    if (this.editAutomaticPaymentsFlowService.wasRecurringAuthorizationFormSuccessfullySaved) {
                        this.editAutomaticPaymentsReviewService.submit().subscribe(() => {
                            if (this.isVulErrorResponse()) {
                                const selectedPayMethod: PaymentItem = this.editAutomaticPaymentsFlowService.selectedPayMethod;
                                const editAutomaticPaymentForm = this.editAutomaticPaymentsFlowService.editAutomaticPaymentForm;
                                const errorDetails = this.editAutomaticPaymentsFlowService.errorDetails;
                                this.editAutomaticPaymentsFlowService.clear();
                                this.editAutomaticPaymentsFlowService.save({ hasCreditCardVulPolicyError: true, selectedPayMethod, editAutomaticPaymentForm, errorDetails });
                                this.navigateToEditAutomaticPayments();
                            } else {
                                this.editAutomaticPaymentsReviewService.deleteCamCache();
                                this.navigateToConfirmationPage();
                            }
                        });
                    } else {
                        this.navigateToConfirmationPage();
                    }
                },
                error: () => {
                    this.navigateToConfirmationPage();
                }
            });
        } else {
            if (this.canDisplayAuthorizationAgreement) {
                this.review.addEzPayAuthGroupControls.markAllAsTouched();
            }
            this.window.scroll(0, 0);
        }
    }

    sendOnClickOfSubmitButtonEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_CLICK_OF_SUBMIT_BTN_EDIT_AUTOMATIC_PAYMENTS_REVIEW_PAGE, {
            paymentPlan: this.editAutomaticPaymentsFlowService.paymentPlan
        });
    }

    private isVulErrorResponse(): boolean {
        const VULErrorResponseText = 'Credit cards cannot be used for variable life policies. Please use a different payment method.';
        return this.editAutomaticPaymentsFlowService.errorDetails?.errorCode === RESPONSE_CODES.ERRORS.SAP.VUL_CREDIT_CARD &&
            this.editAutomaticPaymentsFlowService.errorDetails?.errorMessage === VULErrorResponseText;
    }

    sendOnClickOfAuthAgreementEbiEvent(): void {
        this.eventService.handleEvent(EBIEventType.ON_CLICK_OF_AUTHORIZATION_AGREEMENT_LINK, {
            authAgreementUrl: 'Y',
            paymentPlan: this.editAutomaticPaymentsFlowService.paymentPlan
        });
    }

    get emailAddress(): string {
        return this.paymentFormReview.emailAddress;
    }
}
